import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { DataLayer } from '@grandvisionhq/analytics';
import { Button } from '@grandvisionhq/common';
import { Text } from '@grandvisionhq/elements';
import { StoreFiltersModal } from '@grandvisionhq/fun-store-filters';
import { LocationSearch } from '@grandvisionhq/locations';
import { useIntl } from '@grandvisionhq/state';
import { useStoreFinderContext } from '../../context/StoreFinder/context.js';
import '@grandvisionhq/utils';
import 'lodash/deburr';
import '../../context/StoreFinderSlots/context.js';
import { storesSearchFilterEvent } from '../../data-layer/actions.js';
import useStoreSearchWithoutInventory from '../../hooks/api/use-stores-search-without-inventory.js';
import { useTabs } from '../../hooks/use-tabs.js';
import { mapAppliedStoreFilters } from '../../utils/map-applied-store-filters.js';
import StoreFinderItemContainer from '../StoreFinderItem/StoreFinderItemContainer.js';
import StoreFinderMapContainer from '../StoreFinderMap/StoreFinderMapContainer.js';
import StoreListTopContent from '../StoreListTopContent/StoreListTopContent.js';

const StoreGroupFinderContainerV2 = ({ className , storeFilters: storeFiltersDefault , groupName , urls ,  })=>{
    var ref3;
    const { getLabel , getFormattedLabel  } = useIntl();
    // This ref is used to prevent an initial datalayer push
    const isInitialFilterState = React.useRef(true);
    const { stores , setStores , config , setHoveredStore , isStoresLoading , setIsStoresLoading , appliedStoreFilters , appliedStoreFilterCount , toggleAppliedStoreFilter , clearAppliedStoreFilters ,  } = useStoreFinderContext();
    // Memoization applied because it is in the dependency array of an Effect.
    const mappedAppliedStoreFilters = React.useMemo(()=>mapAppliedStoreFilters(appliedStoreFilters)
    , [
        appliedStoreFilters
    ]);
    const { googleMapsId  } = config;
    const showTabs = useTabs();
    const [isModalOpen, setModalOpen] = React.useState(false);
    const closeModal = ()=>setModalOpen(false)
    ;
    const openModal = ()=>setModalOpen(true)
    ;
    const [searchStores, searchStoresResults] = useStoreSearchWithoutInventory({
        input: {
            groupName,
            filters: mappedAppliedStoreFilters
        }
    });
    var _results;
    const searchedStores = React.useMemo(()=>(_results = searchStoresResults.data.results) !== null && _results !== void 0 ? _results : stores
    , [
        searchStoresResults.data.results,
        stores
    ]);
    var _filters, ref1, ref2;
    // Keep track of the store filters while the data is loading, to prevent
    // the filter modal from being destroyed on filtering
    const storeFilters = React.useMemo(()=>{
        var ref;
        return (ref2 = (ref1 = (_filters = searchStoresResults.data.filters) !== null && _filters !== void 0 ? _filters : (ref = searchStoresResults.previousData) === null || ref === void 0 ? void 0 : ref.availableFilters.filters) !== null && ref1 !== void 0 ? ref1 : storeFiltersDefault) !== null && ref2 !== void 0 ? ref2 : undefined;
    }, [
        searchStoresResults.data.filters,
        (ref3 = searchStoresResults.previousData) === null || ref3 === void 0 ? void 0 : ref3.availableFilters.filters,
        storeFiltersDefault, 
    ]);
    React.useEffect(()=>{
        setIsStoresLoading(true);
        searchStores({
            variables: {
                input: {
                    groupName,
                    filters: mappedAppliedStoreFilters
                }
            }
        });
    }, [
        groupName,
        mappedAppliedStoreFilters,
        searchStores,
        setIsStoresLoading
    ]);
    React.useEffect(()=>{
        if (searchedStores.length === 0) {
            return;
        }
        setStores(searchedStores);
        setIsStoresLoading(false);
    }, [
        searchedStores,
        setIsStoresLoading,
        setStores
    ]);
    /**
   *  Should be refactored and it shouldn't rely on state
   *  We should also utilize the Google Maps API to hover the Pin when hovering the list
   *
   *  Part of future refactor
   *  Story: GAAB-5320
   *  https://jira.grandvision.global/browse/GAAB-5320
   */ const onMouseEnterHandler = (store)=>setHoveredStore(store)
    ;
    const onMouseLeaveHandler = ()=>setHoveredStore(null)
    ;
    const filterButtonText = appliedStoreFilterCount > 0 ? `${getLabel('stores.storeFilters.cta')} (${appliedStoreFilterCount})` : getLabel('stores.storeFilters.cta');
    React.useEffect(()=>{
        // prevent initial filter state from triggering datalayer push
        if (isInitialFilterState.current) {
            isInitialFilterState.current = false;
            return;
        }
        const dataLayerEvent = storesSearchFilterEvent({
            activeFilters: mappedAppliedStoreFilters.map((filter)=>filter.value
            ),
            searchResults: searchedStores.length
        });
        DataLayer.pushEvent(dataLayerEvent);
    }, [
        mappedAppliedStoreFilters,
        searchedStores.length
    ]);
    const shouldShowStoreFilters = storeFilters === null || storeFilters === void 0 ? void 0 : storeFilters.length;
    return /*#__PURE__*/ React.createElement(LocationSearch, {
        className: classnames('store-group-finder', className),
        googleMapsId: googleMapsId,
        showTabs: showTabs,
        searchForm: /*#__PURE__*/ React.createElement(React.Fragment, null, shouldShowStoreFilters ? /*#__PURE__*/ React.createElement("div", {
            className: "store-group-finder__filter-button-wrapper"
        }, /*#__PURE__*/ React.createElement(Button, {
            caption: filterButtonText,
            variant: "ghost",
            icon: "filter",
            onClick: openModal
        }), /*#__PURE__*/ React.createElement(Text, null, getFormattedLabel('stores.page.group.storeFinder.description', {
            hideLabelKey: true,
            labelData: {
                storeCount: stores.length
            }
        })), /*#__PURE__*/ React.createElement(StoreFiltersModal, {
            isOpen: isModalOpen,
            onModalClose: closeModal,
            filters: storeFilters,
            appliedFilters: appliedStoreFilters,
            hasAppliedFilters: appliedStoreFilterCount > 0,
            toggleFilter: toggleAppliedStoreFilter,
            resultsCount: stores.length,
            isLoadingResults: isStoresLoading,
            clearFilters: clearAppliedStoreFilters
        })) : null),
        listTopContent: /*#__PURE__*/ React.createElement(StoreListTopContent, {
            className: "store-group-finder__list-top-content",
            title: getFormattedLabel('stores.page.group.storeFinder.title', groupName ? {
                labelData: {
                    groupName
                }
            } : undefined)
        }),
        loadingListItems: isStoresLoading,
        listItems: searchedStores.map((store)=>/*#__PURE__*/ React.createElement(StoreFinderItemContainer, {
                loading: isStoresLoading,
                key: store.globalStoreId,
                store: store,
                onMouseEnter: onMouseEnterHandler,
                onMouseLeave: onMouseLeaveHandler,
                urls: urls,
                showAttributesAsTags: true
            })
        ),
        mapContent: /*#__PURE__*/ React.createElement(StoreFinderMapContainer, {
            stores: searchedStores,
            infoWindowContentComponent: (props)=>/*#__PURE__*/ React.createElement(StoreFinderItemContainer, swcHelpers.extends({}, props, {
                    urls: urls,
                    view: "map",
                    showAttributesAsTags: true
                }))
        })
    });
};

export { StoreGroupFinderContainerV2 as default };
//# sourceMappingURL=StoreGroupFinderContainerV2.js.map
